import * as React from 'react';
import { computeHalfMonthRange, computeMonthRange, type AvailabilityProfile } from '@ooh/contracts';
import type { MonthPeriodRow } from '../components/month-period-columns-table';
import { deriveMonthMeta } from '../lib/derive-month-meta';
import type { SetProfile, SetStringArray } from './profile-editor-types';

/** month/half_month: the period-columns table's editable source-column + from/to dates per row. */
export function useMonthPeriodEditor(
  profile: AvailabilityProfile,
  setProfile: SetProfile,
  initialColumns: string[],
) {
  // Grows/shrinks alongside `profile.periodColumns` via `addPeriodRow`/
  // `removePeriodRow` (CU-86cb1npz2, gap #2) — unlike the original
  // mount-only seed, a manually-built row has no auto-detected header text
  // to preserve, so its label comes from the caller (e.g. "Okres 2") instead.
  const [monthLabels, setMonthLabels] = React.useState<string[]>(initialColumns);

  const monthRows: MonthPeriodRow[] = profile.periodColumns.map((pc, i) => ({
    id: `period-${i}`,
    month: monthLabels[i] ?? pc.column,
    sourceColumn: pc.column,
    from: pc.periodStart,
    to: pc.periodEnd,
  }));

  function updatePeriodRow(id: string, patch: Partial<Omit<MonthPeriodRow, 'id' | 'month'>>) {
    const idx = Number(id.replace('period-', ''));
    setProfile((p) => ({
      ...p,
      periodColumns: p.periodColumns.map((pc, i) =>
        i !== idx
          ? pc
          : {
              column: patch.sourceColumn ?? pc.column,
              periodStart: patch.from ?? pc.periodStart,
              periodEnd: patch.to ?? pc.periodEnd,
            },
      ),
    }));
  }

  /** Overrides one row's resolved year in case the resolver's "first future
   * occurrence" guess (see availability-profile-resolution) picked the wrong
   * one — recomputes periodStart/periodEnd client-side, no server round-trip. */
  function updatePeriodYear(id: string, nextYear: number) {
    const idx = Number(id.replace('period-', ''));
    setProfile((p) => {
      const pc = p.periodColumns[idx];
      const meta = pc && deriveMonthMeta(pc.periodStart, pc.periodEnd);
      if (!pc || !meta) return p;
      const range = meta.half
        ? computeHalfMonthRange(nextYear, meta.month, meta.half)
        : computeMonthRange(nextYear, meta.month);
      return {
        ...p,
        periodColumns: p.periodColumns.map((row, i) =>
          i !== idx ? row : { ...row, periodStart: range.start, periodEnd: range.end },
        ),
      };
    });
  }

  const manualRows = useManualPeriodRows(setProfile, setMonthLabels);

  return { monthRows, updatePeriodRow, updatePeriodYear, ...manualRows };
}

/** Manual from-scratch period-row construction (CU-86cb1npz2, gap #2) — the
 * low-confidence fallback for files whose period headers don't auto-parse
 * (e.g. Roman-numeral month abbreviations). Extracted out of
 * `useMonthPeriodEditor` to keep that function under the repo's
 * per-function line-count budget. */
function useManualPeriodRows(setProfile: SetProfile, setMonthLabels: SetStringArray) {
  /** Appends a blank period row (empty sourceColumn/from/to, left for the
   * operator to fill in). `label` is the caller-supplied (translated) row
   * identifier since a manually-built row has no detected header text to
   * reuse as `month`. */
  function addPeriodRow(label: string) {
    setProfile((p) => ({
      ...p,
      periodColumns: [...p.periodColumns, { column: '', periodStart: '', periodEnd: '' }],
    }));
    setMonthLabels((prev) => [...prev, label]);
  }

  /** Removes one manually-added period row by its `period-<index>` id. */
  function removePeriodRow(id: string) {
    const idx = Number(id.replace('period-', ''));
    setProfile((p) => ({
      ...p,
      periodColumns: p.periodColumns.filter((_, i) => i !== idx),
    }));
    setMonthLabels((prev) => prev.filter((_, i) => i !== idx));
  }

  /** Clears `monthLabels` back to empty — called by `useTimeEncodingEditor`
   * at the same point `periodColumns` gets reset to `[]`, so the two arrays
   * never drift out of index alignment across a month/half_month round-trip
   * (review round 1, CU-86cb1npz2). */
  function resetMonthLabels() {
    setMonthLabels([]);
  }

  return { addPeriodRow, removePeriodRow, resetMonthLabels };
}
